/**
 * Store persistence and utility functions
 */

export const STORE_STORAGE_KEYS = {
  SELECTED_STORE: 'beautello_selected_store',
  STORE_PREFERENCES: 'beautello_store_preferences',
  RECENT_STORES: 'beautello_recent_stores'
} as const;

export interface StorePreferences {
  selectedStoreId: string;
  lastAccessTime: string;
  favoriteStores: string[];
}

export interface RecentStore {
  storeId: string;
  storeName: string;
  lastAccessTime: string;
  accessCount: number;
}

/**
 * Get the currently selected store from localStorage
 */
export const getSelectedStoreFromStorage = (): string | null => {
  try {
    return localStorage.getItem(STORE_STORAGE_KEYS.SELECTED_STORE);
  } catch (error) {
    console.warn('Failed to get selected store from localStorage:', error);
    return null;
  }
};

/**
 * Save the selected store to localStorage
 */
export const saveSelectedStoreToStorage = (storeId: string): void => {
  try {
    localStorage.setItem(STORE_STORAGE_KEYS.SELECTED_STORE, storeId);
    
    // Also update store preferences
    const preferences = getStorePreferences();
    preferences.selectedStoreId = storeId;
    preferences.lastAccessTime = new Date().toISOString();
    saveStorePreferences(preferences);
    
  } catch (error) {
    console.warn('Failed to save selected store to localStorage:', error);
  }
};

/**
 * Get store preferences from localStorage
 */
export const getStorePreferences = (): StorePreferences => {
  try {
    const stored = localStorage.getItem(STORE_STORAGE_KEYS.STORE_PREFERENCES);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.warn('Failed to get store preferences from localStorage:', error);
  }
  
  // Return default preferences
  return {
    selectedStoreId: '',
    lastAccessTime: new Date().toISOString(),
    favoriteStores: []
  };
};

/**
 * Save store preferences to localStorage
 */
export const saveStorePreferences = (preferences: StorePreferences): void => {
  try {
    localStorage.setItem(STORE_STORAGE_KEYS.STORE_PREFERENCES, JSON.stringify(preferences));
  } catch (error) {
    console.warn('Failed to save store preferences to localStorage:', error);
  }
};

/**
 * Add a store to favorites
 */
export const addStoreToFavorites = (storeId: string): void => {
  const preferences = getStorePreferences();
  if (!preferences.favoriteStores.includes(storeId)) {
    preferences.favoriteStores.push(storeId);
    saveStorePreferences(preferences);
  }
};

/**
 * Remove a store from favorites
 */
export const removeStoreFromFavorites = (storeId: string): void => {
  const preferences = getStorePreferences();
  preferences.favoriteStores = preferences.favoriteStores.filter(id => id !== storeId);
  saveStorePreferences(preferences);
};

/**
 * Check if a store is in favorites
 */
export const isStoreFavorite = (storeId: string): boolean => {
  const preferences = getStorePreferences();
  return preferences.favoriteStores.includes(storeId);
};

/**
 * Track recent store access
 */
export const trackStoreAccess = (storeId: string, storeName: string): void => {
  try {
    const stored = localStorage.getItem(STORE_STORAGE_KEYS.RECENT_STORES);
    let recentStores: RecentStore[] = stored ? JSON.parse(stored) : [];
    
    // Find existing entry or create new one
    const existingIndex = recentStores.findIndex(store => store.storeId === storeId);
    
    if (existingIndex >= 0) {
      // Update existing entry
      recentStores[existingIndex].lastAccessTime = new Date().toISOString();
      recentStores[existingIndex].accessCount += 1;
      recentStores[existingIndex].storeName = storeName; // Update name in case it changed
    } else {
      // Add new entry
      recentStores.push({
        storeId,
        storeName,
        lastAccessTime: new Date().toISOString(),
        accessCount: 1
      });
    }
    
    // Sort by last access time (most recent first) and keep only top 10
    recentStores = recentStores
      .sort((a, b) => new Date(b.lastAccessTime).getTime() - new Date(a.lastAccessTime).getTime())
      .slice(0, 10);
    
    localStorage.setItem(STORE_STORAGE_KEYS.RECENT_STORES, JSON.stringify(recentStores));
  } catch (error) {
    console.warn('Failed to track store access:', error);
  }
};

/**
 * Get recent stores
 */
export const getRecentStores = (): RecentStore[] => {
  try {
    const stored = localStorage.getItem(STORE_STORAGE_KEYS.RECENT_STORES);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.warn('Failed to get recent stores from localStorage:', error);
    return [];
  }
};

/**
 * Clear all store data from localStorage
 */
export const clearStoreData = (): void => {
  try {
    Object.values(STORE_STORAGE_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });
    console.log('✅ All store data cleared from localStorage');
  } catch (error) {
    console.warn('Failed to clear store data from localStorage:', error);
  }
};

/**
 * Export store data for backup
 */
export const exportStoreData = (): string => {
  try {
    const data = {
      preferences: getStorePreferences(),
      recentStores: getRecentStores(),
      selectedStore: getSelectedStoreFromStorage(),
      exportDate: new Date().toISOString()
    };
    return JSON.stringify(data, null, 2);
  } catch (error) {
    console.warn('Failed to export store data:', error);
    return '{}';
  }
};

/**
 * Import store data from backup
 */
export const importStoreData = (jsonData: string): boolean => {
  try {
    const data = JSON.parse(jsonData);
    
    if (data.preferences) {
      saveStorePreferences(data.preferences);
    }
    
    if (data.recentStores) {
      localStorage.setItem(STORE_STORAGE_KEYS.RECENT_STORES, JSON.stringify(data.recentStores));
    }
    
    if (data.selectedStore) {
      saveSelectedStoreToStorage(data.selectedStore);
    }
    
    console.log('✅ Store data imported successfully');
    return true;
  } catch (error) {
    console.warn('Failed to import store data:', error);
    return false;
  }
};
